import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  startDistancesJob,
  downloadDistancesResult,
  getDistancesStreamTicket,
} from "../../services/api";
import { API_BASE_URL } from "../../services/apiClient";

const TOOL_COLOR = "#00897B";

type Phase = "idle" | "uploading" | "processing" | "done" | "error";

export const DistancesTool: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const evtSourceRef = useRef<EventSource | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [vettoreIntra, setVettoreIntra] = useState<"GOMMA" | "TRENO">("GOMMA");
  const [vettoreInter, setVettoreInter] = useState<"NAVE" | "AEREO">("NAVE");
  const [raggioKm, setRaggioKm] = useState<string>("0");

  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState<number>(0);
  const [progressMsg, setProgressMsg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultFilename, setResultFilename] = useState<string>("");

  // Close any open SSE stream when component unmounts
  useEffect(() => {
    return () => {
      evtSourceRef.current?.close();
    };
  }, []);

  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    reset();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (phase !== "uploading" && phase !== "processing") setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (phase === "uploading" || phase === "processing") return;
    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;
    const ext = "." + dropped.name.split(".").pop()?.toLowerCase();
    if ([".xlsx", ".xls"].includes(ext)) {
      setFile(dropped);
      reset();
    }
  };

  const reset = () => {
    evtSourceRef.current?.close();
    evtSourceRef.current = null;
    setPhase("idle");
    setProgress(0);
    setProgressMsg("");
    setError(null);
    setResultBlob(null);
    setResultFilename("");
  };

  const handleProcess = async () => {
    if (!file) return;
    reset();
    setPhase("uploading");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("vettore_intracontinentale", vettoreIntra);
      formData.append("vettore_intercontinentale", vettoreInter);
      formData.append(
        "raggio_approssimazione_km",
        String(parseFloat(raggioKm) || 0)
      );

      const { job_id } = await startDistancesJob(formData);

      setPhase("processing");
      await openStream(job_id);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Errore durante il caricamento del file";
      setError(msg);
      setPhase("error");
    }
  };

  const openStream = async (jobId: string) => {
    // Ticket breve riferito a questo job (mai l'access token in query string)
    const ticket = await getDistancesStreamTicket(jobId);
    const url = `${API_BASE_URL}/distances/stream/${jobId}?ticket=${encodeURIComponent(ticket)}`;
    const es = new EventSource(url);
    evtSourceRef.current = es;

    es.onmessage = async (e) => {
      let data: any;
      try {
        data = JSON.parse(e.data);
      } catch {
        return;
      }

      if (data.status === "processing") {
        setProgress(Math.round((data.progress ?? 0) * 100));
        setProgressMsg(data.message ?? "");
      } else if (data.status === "done") {
        es.close();
        setProgress(100);
        setProgressMsg("Completato.");
        try {
          const { blob, filename } = await downloadDistancesResult(jobId);
          setResultBlob(blob);
          setResultFilename(filename);
          setPhase("done");
        } catch (err: any) {
          setError(err?.message || "Errore nel download del risultato");
          setPhase("error");
        }
      } else if (data.status === "error") {
        es.close();
        setError(data.error || "Errore durante l'elaborazione");
        setPhase("error");
      }
    };

    es.onerror = () => {
      es.close();
      if (phase !== "done") {
        setError("Connessione al server interrotta. Riprova.");
        setPhase("error");
      }
    };
  };

  const handleDownload = () => {
    if (!resultBlob) return;
    const url = URL.createObjectURL(resultBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = resultFilename || "risultato.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const isRunning = phase === "uploading" || phase === "processing";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 560,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* File upload */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            File Excel di input
          </Typography>
          <Button
            component="a"
            href="/assets/Template.xlsx"
            download="Template.xlsx"
            size="small"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: "1rem !important" }} />}
            sx={{
              fontSize: "0.72rem",
              textTransform: "none",
              color: TOOL_COLOR,
              borderColor: TOOL_COLOR + "88",
              borderRadius: "8px",
              py: 0.3,
              px: 1,
              border: "1px solid",
              "&:hover": { bgcolor: TOOL_COLOR + "10", borderColor: TOOL_COLOR },
            }}
          >
            Scarica template
          </Button>
        </Box>
        <Box
          onClick={() => !isRunning && fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: "2px dashed",
            borderColor: isDragging ? TOOL_COLOR : file ? TOOL_COLOR : "rgba(0,0,0,0.18)",
            borderRadius: "12px",
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            cursor: isRunning ? "default" : "pointer",
            bgcolor: isDragging ? TOOL_COLOR + "18" : file ? TOOL_COLOR + "08" : "background.paper",
            transition: "all 0.2s",
            "&:hover": isRunning
              ? {}
              : { borderColor: TOOL_COLOR, bgcolor: TOOL_COLOR + "08" },
          }}
        >
          <UploadFileIcon
            sx={{
              fontSize: "2rem",
              color: file ? TOOL_COLOR : isDragging ? TOOL_COLOR : "text.disabled",
            }}
          />
          {file ? (
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: TOOL_COLOR }}
              >
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(file.size / 1024).toFixed(1)} KB
                {!isRunning && " — clicca per cambiare"}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {isDragging ? "Rilascia il file qui" : "Clicca o trascina qui"}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Formato .xlsx o .xls
              </Typography>
            </Box>
          )}
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>

      {/* Parameters */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <FormControl fullWidth size="small" disabled={isRunning}>
          <InputLabel>Vettore intracontinentale</InputLabel>
          <Select
            value={vettoreIntra}
            label="Vettore intracontinentale"
            onChange={(e) =>
              setVettoreIntra(e.target.value as "GOMMA" | "TRENO")
            }
            sx={{ borderRadius: "8px" }}
          >
            <MenuItem value="GOMMA">Gomma</MenuItem>
            <MenuItem value="TRENO">Treno</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={isRunning}>
          <InputLabel>Vettore intercontinentale</InputLabel>
          <Select
            value={vettoreInter}
            label="Vettore intercontinentale"
            onChange={(e) =>
              setVettoreInter(e.target.value as "NAVE" | "AEREO")
            }
            sx={{ borderRadius: "8px" }}
          >
            <MenuItem value="NAVE">Nave</MenuItem>
            <MenuItem value="AEREO">Aereo</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Raggio di approssimazione */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          label="Raggio di approssimazione (km)"
          size="small"
          type="number"
          value={raggioKm}
          disabled={isRunning}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "" || parseFloat(v) >= 0) setRaggioKm(v);
          }}
          slotProps={{
            input: {
              inputProps: { min: 0, step: 1 },
              sx: { borderRadius: "8px" },
            },
          }}
          sx={{ flex: 1 }}
        />
        <Tooltip
          title="Se > 0, le spedizioni la cui distanza in linea d'aria tra origine e destinazione è entro questo raggio vengono considerate consegne locali e i km vengono impostati a 0. Lascia 0 per disabilitare."
          arrow
          placement="top"
        >
          <InfoOutlinedIcon
            sx={{ fontSize: "1.1rem", color: "text.disabled", cursor: "help" }}
          />
        </Tooltip>
      </Box>

      {/* Progress */}
      {isRunning && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {phase === "uploading"
                ? "Caricamento file…"
                : progressMsg || "Elaborazione in corso…"}
            </Typography>
            {phase === "processing" && (
              <Typography
                variant="caption"
                sx={{ color: TOOL_COLOR, fontWeight: 600 }}
              >
                {progress}%
              </Typography>
            )}
          </Box>
          <LinearProgress
            variant={
              phase === "uploading" || progress === 0
                ? "indeterminate"
                : "determinate"
            }
            value={progress}
            sx={{
              borderRadius: 4,
              height: 6,
              bgcolor: TOOL_COLOR + "22",
              "& .MuiLinearProgress-bar": { bgcolor: TOOL_COLOR },
            }}
          />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          onClose={reset}
          sx={{ borderRadius: "10px" }}
        >
          {error}
        </Alert>
      )}

      {/* Success */}
      {phase === "done" && resultBlob && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon />}
          sx={{ borderRadius: "10px" }}
        >
          Elaborazione completata.
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          fullWidth
          disabled={!file || isRunning}
          onClick={handleProcess}
          sx={{
            borderRadius: "10px",
            py: 1.2,
            bgcolor: TOOL_COLOR,
            "&:hover": { bgcolor: "#00695C" },
          }}
        >
          Elabora
        </Button>

        {resultBlob && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleDownload}
            startIcon={<DownloadIcon />}
            sx={{
              borderRadius: "10px",
              py: 1.2,
              borderColor: TOOL_COLOR,
              color: TOOL_COLOR,
              "&:hover": { borderColor: "#00695C", color: "#00695C" },
            }}
          >
            Scarica risultato
          </Button>
        )}
      </Box>
    </Box>
  );
};
